import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { get_return_data } from "../../reduxData/user/userAction";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../hooks/formatPrice";
import { formatDateTime } from "../../hooks/formatDateTime";
import { languageFields } from "../../hooks/LanguageFields";
import TranslatedText from "../../hooks/TranslatedText";
import EmptyData from "../../components/EmptyData";

const ReturnOrder = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { return_items_data, total_return_items } = useSelector((state) => state.user);
    const [width, setWidth] = useState(window.innerWidth);
    const [expandedRows, setExpandedRows] = useState({});
    const isMobile = width <= 750;
    const { REACT_APP_IMAGE_URL, REACT_APP_REDIRECT_URL } = process.env;

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchReturns = async () => {
            await get_return_data(dispatch, 1, 10);
        };
        fetchReturns();
    }, [dispatch]);

    const getImageUrl = (url) => {
        if (!url) return null;
        return url.startsWith("http") || url.startsWith("https")
            ? url
            : `${REACT_APP_IMAGE_URL}${url}`;
    };

    const getOrderId = (item) => item?.order_id || item?.orderId || item?.orderid || "-";

    const toggleRow = (orderId) => {
        setExpandedRows((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
    };

    const handleProductDetail = (slug) => {
        if (slug) {
            window.open(
                `${REACT_APP_REDIRECT_URL}auction/${slug}`,
                "_blank",
                "noopener,noreferrer"
            );
        }
    };

    const handleDetail = (item) => {
        const returnId = item?.id || item?.return_request_id;
        if (returnId) {
            navigate(`/return/${returnId}`);
        }
    };

    const returnTagClass = (status) => {
        const s = String(status || "").toUpperCase();
        if (s === "APPROVED" || s === "COMPLETED" || s === "REFUNDED" || s === "RETURN_RECEIVED") return "approved";
        if (s === "REJECTED") return "rejected";
        return "pending";
    };

    const returnStatusLabel = (status) => {
        if (!status) return "";
        return status .toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const renderExpandedItem = (retItem) => {
        const itemData = retItem?.itemData || {};
        const variantData = retItem?.variantData || {};
        const title = languageFields(itemData, "title", i18n) || itemData?.title || "Product";
        // const itemPrice = variantData?.bid_amount || parentItem?.original_retail_price || parentItem?.amount;
        const itemPrice = variantData?.bid_amount != 0 ? variantData.bid_amount : itemData?.original_retail_price ?? null;
        // Prioritize variant images, then ai_images
        const imageUrl = getImageUrl(
            variantData?.images?.[0] || itemData?.ai_images?.[0] || itemData?.image_urls?.[0]
        );

        return (
            <div className="si-sub-product" key={retItem?.item_id || retItem?.id}>
                <img
                    src={imageUrl}
                    alt={title}
                    className="si-sub-img"
                />
                <div className="si-sub-info">
                    <span
                        className="si-sub-title"
                        onClick={() => handleProductDetail(itemData?.slug)}
                    >
                        {title}
                    </span>
                </div>
                <div className="si-sub-chips">
                    {variantData.size_name && (
                        <span className="od2-attr-chip">
                            {t("returnOrder.Size")}: <strong>{variantData.size_name}</strong>
                        </span>
                    )}
                    {variantData.color_name && (
                        <span className="od2-attr-chip">
                            {t("returnOrder.Color")}: <strong><TranslatedText text={variantData.color_name} /></strong>
                        </span>
                    )}
                    {itemPrice && (
                        <span className="od2-attr-chip--price">
                            {formatPrice(itemPrice, i18n)}
                        </span>
                    )}
                    {retItem.quantity && (
                        <span className="od2-attr-chip">
                            {t("returnOrder.Quantity")}: <strong>{retItem.quantity}</strong>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Desktop Table
    const DesktopTable = () => (
        <div className="si-table-wrapper">
            <table className="si-table">
                <colgroup>
                    <col className="si-col-toggle" />
                    <col className="si-col-order" />
                    <col className="si-col-order" />
                    <col className="si-col-items" />
                    <col className="si-col-reason" />
                    <col className="si-col-status" />
                    <col className="si-col-date" />
                    <col className="si-col-action" />
                </colgroup>

                <thead className="si-thead">
                    <tr>
                        <th className="si-th si-th--center" />
                        <th className="si-th">{t("returnOrder.ReturnID")}</th>
                        <th className="si-th">{t("returnOrder.OrderId")}</th>
                        <th className="si-th si-th--center">{t("returnOrder.NoofItems") || "No. of Items"}</th>
                        <th className="si-th">{t("returnOrder.ReturnReason") || "Reason"}</th>
                        <th className="si-th si-th--center">{t("returnOrder.Status")}</th>
                        <th className="si-th si-th--center">{t("returnOrder.ReturnDate") || "Return Date"}</th>
                        <th className="si-th si-th--center">{t("returnOrder.Action")}</th>
                    </tr>
                </thead>

                <tbody>
                    {return_items_data.map((item) => {
                        const orderId = getOrderId(item);
                        const returnId = item?.id;
                        const returnedItems = item?.returned_items || item?.return_items || [item];
                        const isExpanded = !!expandedRows[returnId];
                        const reasonText = <TranslatedText text={item?.comment || item?.reason || item?.request_reason} />
                        const statusText = item?.return_status || "pending";

                        return (
                            <React.Fragment key={returnId}>
                                <tr className={`si-row si-row--parent ${isExpanded ? "si-row--expanded" : ""}`}>
                                    <td className="si-td si-td--center">
                                        {returnedItems.length > 0 && (
                                            <button
                                                className={`si-toggle-btn ${isExpanded ? "si-toggle-btn--open" : ""}`}
                                                onClick={() => toggleRow(returnId)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}
                                    </td>
                                    <td className="si-td">
                                        <span className="si-order-id">#{returnId}</span>
                                    </td>
                                    <td className="si-td">
                                        <span className="si-order-id">#{orderId}</span>
                                    </td>

                                    <td className="si-td si-td--center">
                                        <button
                                            className={`si-count-badge ${returnedItems.length > 0 ? "si-count-badge--clickable" : ""}`}
                                            onClick={() => returnedItems.length > 0 && toggleRow(returnId)}
                                        >
                                            {returnedItems.length} {returnedItems.length === 1 ? t("returnOrder.Item") : t("returnOrder.Items")}
                                        </button>
                                    </td>

                                    <td className="si-td">
                                        <div className="si-truncated-text">{reasonText}</div>
                                    </td>

                                    <td className="si-td si-td--center">
                                        <span className={`od2-dispute-status-tag ${returnTagClass(statusText)}`}>
                                            {/* {statusText.toUpperCase()} */}
                                            <TranslatedText text={returnStatusLabel(statusText)} />
                                        </span>
                                    </td>

                                    <td className="si-td si-td--center si-date">
                                        {formatDateTime(item?.created_at, i18n)}
                                    </td>

                                    <td className="si-td si-td--center">
                                        <button className="si-view-btn" onClick={() => handleDetail(item)}>
                                            {t("returnOrder.View")}
                                        </button>
                                    </td>
                                </tr>

                                {isExpanded && returnedItems.length > 0 && (
                                    <tr className="si-row si-row--child">
                                        <td className="si-td si-td--indent" />
                                        <td className="si-td si-sub-full" colSpan={7}>
                                            <div className="si-card-items">
                                                {returnedItems.map(renderExpandedItem)}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    // Mobile Cards (Clean & Consistent)
    const MobileCards = () => (
        <div className="si-mobile-list">
            {return_items_data.map((item) => {
                const orderId = getOrderId(item);
                const returnId = item?.id;
                const returnedItems = item?.returned_items || item?.return_items || [item];
                const isExpanded = !!expandedRows[returnId];
                const reasonText = <TranslatedText text={item?.comment || item?.reason || item?.request_reason} />
                const statusText = item?.return_status || "pending";
                const primaryItem = returnedItems[0] || {};
                const primaryTitle = languageFields(primaryItem?.itemData, "title", i18n) || primaryItem?.title || "-";

                return (
                    <div key={returnId} className="si-card">
                        <div className="si-card-header">
                            <div>
                                <span className="si-card-order-id">#{orderId}</span>
                                <div className="si-card-date">
                                    {formatDateTime(item?.created_at, i18n)}
                                </div>
                            </div>
                            <span className={`badge px-3 py-2 ${returnTagClass(statusText)}`}>
                                {statusText.toUpperCase()}
                            </span>
                        </div>

                        <div className="si-card-badges">
                            <div className="si-card-badge-group">
                                <span className="si-card-badge-label">Reason</span>
                                <span className="text-muted">{reasonText}</span>
                            </div>
                        </div>

                        {returnedItems.length > 0 && (
                            <button
                                className="si-card-expand-btn"
                                onClick={() => toggleRow(returnId)}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
                                    className={isExpanded ? "si-icon--flipped" : ""}>
                                    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {isExpanded ? "Hide Items" : `Show ${returnedItems.length} Items`}
                            </button>
                        )}

                        {isExpanded && returnedItems.length > 0 && (
                            <div className="si-card-items">
                                {returnedItems.map(renderExpandedItem)}
                            </div>
                        )}

                        <div className="si-card-footer">
                            <button className="si-view-btn" onClick={() => handleDetail(item)}>
                                {t("returnOrder.View")}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className="si-tab-pane">
            <div className="si-section-header">
                <h3 className="si-section-title">
                    <span className="si-section-icon">↩️</span>
                    {t("returnOrder.ReturnOrders") || "Return Orders"}
                </h3>
            </div>

            {total_return_items > 0 || return_items_data?.length > 0 ? (
                isMobile ? <MobileCards /> : <DesktopTable />
            ) : (
                <EmptyData />
            )}
        </div>
    );
};

export default ReturnOrder;

